import { Component, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { InformationBoxComponent } from '@shared/information-box/information-box.component';
import {
  SyrfDialogActionsDirective,
  SyrfDialogLayoutComponent,
} from '@shared/dialog/syrf-dialog-layout.component';
import {
  SyrfDialogService,
  closeButtonClicks,
  injectSyrfDialogRef,
} from '@shared/dialog/syrf-dialog.service';
import { firstValueFrom } from 'rxjs';

/** The heading openers pass as the dialog title. */
export const ERROR_RECOVERY_DIALOG_TITLE = 'SyRF Error Detected';

/**
 * Offered after an unhandled error: Refresh closes with `true`, "Don't refresh now" with `false`.
 * Escape and the backdrop cannot close it (`disableClose`); the Expressive close button closes with
 * `undefined`, which `open` treats like `false`. `disableClose` blocks that button too
 * (camaradesuk/m3e-angular#108), so its clicks are routed to the close here.
 */
@Component({
  selector: 'app-error-recovery-dialog',
  templateUrl: './error-recovery-dialog.component.html',
  styleUrls: ['./error-recovery-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SyrfDialogLayoutComponent,
    SyrfDialogActionsDirective,
    MatButtonModule,
    MatIconModule,
    InformationBoxComponent,
  ],
})
export class ErrorRecoveryDialogComponent {
  static async open(dialog: SyrfDialogService) {
    // After Sentry dialog closes, show Material Design recovery dialog
    const dialogRef = dialog.open<
      ErrorRecoveryDialogComponent,
      unknown,
      boolean
    >(ErrorRecoveryDialogComponent, {
      mode: 'adaptive',
      title: ERROR_RECOVERY_DIALOG_TITLE,
      width: '600px',
      disableClose: true, // Prevent closing by clicking outside
    });

    const shouldRefresh = await firstValueFrom(dialogRef.afterClosed());

    if (shouldRefresh === true) {
      // User chose to refresh
      localStorage.setItem('app_recovered_from_crash', 'true');
      window.location.reload();
    } else if (shouldRefresh === false) {
      // User chose not to refresh - only need immediate warning, no persistence needed
      // The localStorage flag is only for showing warnings after navigation/refresh
      // For immediate same-session warning, just dispatch the event
      window.dispatchEvent(
        new CustomEvent('app-unstable-state', {
          detail: { showWarning: true },
        })
      );
    }
    // If shouldRefresh is undefined, user closed dialog without choice - treat as "continue"
    else {
      window.dispatchEvent(
        new CustomEvent('app-unstable-state', {
          detail: { showWarning: true },
        })
      );
    }
  }
  private readonly _dialogRef = injectSyrfDialogRef<
    ErrorRecoveryDialogComponent,
    boolean
  >();

  constructor() {
    closeButtonClicks(this._dialogRef)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this._dialogRef.close());
  }

  onRefreshClick(): void {
    this._dialogRef.close(true);
  }

  onContinueClick(): void {
    this._dialogRef.close(false);
  }

  onNoClick(): void {
    this._dialogRef.close(false);
  }
}
